/** Locale-aware formatting for the catalog. Locale follows the active i18next language. */

import i18n from '@/shared/i18n/i18n';

/**
 * Map an i18next 2-/5-letter locale code to a full BCP-47 tag that Intl accepts.
 * Falls back to 'en-US' for any unrecognised code.
 */
function getIntlLocale(): string {
  const lng = i18n.language;
  const map: Record<string, string> = {
    en: 'en-US',
    ru: 'ru-RU',
    'zh-CN': 'zh-CN',
    'zh-TW': 'zh-TW',
  };
  return map[lng] ?? 'en-US';
}

/** Format a money amount in RUB, or an em dash when absent. */
export function formatPrice(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(getIntlLocale(), {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(value);
}

/** Format a plain count (thousands separated). */
export function formatNumber(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(getIntlLocale()).format(value);
}

/** Format an ISO timestamp, or an em dash when absent/invalid. */
export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/** Human-readable byte size. */
export function formatBytes(bytes?: number | null): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}
